import { useEffect, useRef } from 'react';

import { fg } from '@confluence/feature-gating';

/**
 * Fires a snippets Awareness (screen-viewed) impression exactly once when a
 * surface mounts, behind the `platform_editor_blocks_patch_2` gate.
 *
 * Shared by the snippet panel and the template gallery so the fire-once + gate
 * behaviour stays consistent across surfaces. A per-instance ref guards against
 * double-firing on re-render; because the ref is recreated for each component
 * instance, the event correctly fires again after an unmount + remount.
 *
 * @param fireScreenViewedAnalytic - The memoised impression emitter from
 *   `useSnippetAnalytics`. Must have a stable identity so the mount-only effect
 *   does not re-run on every parent render.
 */
export const useSnippetViewedOnce = (fireScreenViewedAnalytic: () => void): void => {
	const hasFiredViewedRef = useRef(false);

	useEffect(() => {
		if (!fg('platform_editor_blocks_patch_2')) {
			return;
		}
		if (hasFiredViewedRef.current) {
			return;
		}
		hasFiredViewedRef.current = true;
		fireScreenViewedAnalytic();
	}, [fireScreenViewedAnalytic]);
};
